import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  DocumentArrowDownIcon,
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';

import KPICard from '../components/KPICard';
import ChartContainer from '../components/ChartContainer';
import PieChart from '../components/charts/PieChart';
import LineChart from '../components/charts/LineChart';
import companyDataService from '../services/companyDataService';
import exportService from '../services/exportService';
import pdfExportService from '../services/pdfExportService';

const Statistics = () => {
  const [stats, setStats] = useState({
    totalProdutos: 0,
    totalConsultas: 0,
    totalDispositivos: 0,
    totalFiliais: 0,
    mediaConsultasPorProduto: 0,
    produtoMaisConsultado: null,
    filialMaisAtiva: null,
    dispositivoMaisUsado: null
  });
  
  const [consultasPorFilial, setConsultasPorFilial] = useState([]);
  const [produtosPorCategoria, setProdutosPorCategoria] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [estatisticasFiliais, setEstatisticasFiliais] = useState([]);
  const [estatisticasDispositivos, setEstatisticasDispositivos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStatisticsData();
  }, []);

  const loadStatisticsData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await companyDataService.loadData();
      updateStatisticsData();
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      setError('Erro ao carregar dados estatísticos. Verifique se o arquivo JSON está disponível.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatisticsData = () => {
    const basicStats = companyDataService.getStats();
    const consultasFilial = companyDataService.getConsultasPorFilial();
    const produtosCategoria = companyDataService.getProdutosPorCategoria();
    const timeline = companyDataService.getTimelineConsultas();
    const statsFiliais = companyDataService.getEstatisticasPorFilial();
    const statsDispositivos = companyDataService.getEstatisticasPorDispositivo();

    // Encontrar produto mais consultado
    const produtos = companyDataService.getAllData();
    const produtoMaisConsultado = produtos.reduce((max, produto) => 
      (produto.num_consultas || 0) > (max.num_consultas || 0) ? produto : max
    );

    // Encontrar filial mais ativa
    const filialMaisAtiva = statsFiliais.length > 0 ? statsFiliais[0] : null;

    // Encontrar dispositivo mais usado
    const dispositivoMaisUsado = statsDispositivos.length > 0 ? statsDispositivos[0] : null;

    setStats({
      ...basicStats,
      produtoMaisConsultado,
      filialMaisAtiva,
      dispositivoMaisUsado
    });

    setConsultasPorFilial(consultasFilial);
    setProdutosPorCategoria(produtosCategoria);
    setTimelineData(timeline);
    setEstatisticasFiliais(statsFiliais);
    setEstatisticasDispositivos(statsDispositivos);
  };

  const handleExportStatistics = async () => {
    try {
      const dataToExport = {
        estatisticasGerais: stats,
        consultasPorFilial,
        produtosPorCategoria,
        estatisticasFiliais,
        estatisticasDispositivos
      };
      
      await exportService.exportToExcel(dataToExport, `estatisticas_detalhadas_${Date.now()}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar estatísticas:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const dataToExport = {
        estatisticasGerais: stats,
        consultasPorFilial,
        produtosPorCategoria,
        estatisticasFiliais,
        estatisticasDispositivos
      };
      
      await pdfExportService.exportStatisticsReport(dataToExport);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
    }
  };

  const handleUpdateData = async () => {
    try {
      await companyDataService.updateFromApi();
      updateStatisticsData();
      return { success: true, message: 'Dados atualizados com sucesso!' };
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Carregando Estatísticas</h3>
          <p className="text-dark-text-secondary">
            Processando dados estatísticos...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="card-dark p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-danger rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Erro ao Carregar</h3>
          <p className="text-dark-text-secondary mb-4">{error}</p>
          <button
            onClick={loadStatisticsData}
            className="btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
             {/* Cabeçalho */}
       <div className="flex justify-between items-start">
         <div>
           <h1 className="text-2xl font-bold text-dark-text mb-2">
             Estatísticas Detalhadas
           </h1>
           <p className="text-dark-text-secondary">
             Análise completa dos dados da empresa {companyDataService.companyId}
           </p>
         </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportPDF}
              className="btn-primary flex items-center gap-2"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Exportar PDF
            </button>
            <button
              onClick={handleExportStatistics}
              className="btn-secondary flex items-center gap-2"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Exportar Excel
            </button>
          </div>
       </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total de Produtos"
          value={companyDataService.formatNumber(stats.totalProdutos)}
          icon={ChartBarIcon}
          color="primary"
        />
        <KPICard
          title="Total de Consultas"
          value={companyDataService.formatNumber(stats.totalConsultas)}
          icon={ArrowTrendingUpIcon}
          color="success"
        />
        <KPICard
          title="Dispositivos Ativos"
          value={companyDataService.formatNumber(stats.totalDispositivos)}
          icon={DevicePhoneMobileIcon}
          color="warning"
        />
        <KPICard
          title="Filiais"
          value={companyDataService.formatNumber(stats.totalFiliais)}
          icon={BuildingOfficeIcon}
          color="info"
        />
      </div>

      {/* KPIs Secundários */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KPICard
          title="Média Consultas/Produto"
          value={stats.mediaConsultasPorProduto.toFixed(1)}
          icon={ChartBarIcon}
          color="secondary"
        />
        <KPICard
          title="Produto Mais Consultado"
          value={stats.produtoMaisConsultado ? stats.produtoMaisConsultado.num_consultas : 0}
          icon={ChartBarIcon}
          color="success"
          subtitle={stats.produtoMaisConsultado ? 
            stats.produtoMaisConsultado.description?.substring(0, 40) + '...' : 
            'Nenhum produto'
          }
        />
        <KPICard
          title="Filial Mais Ativa"
          value={stats.filialMaisAtiva ? stats.filialMaisAtiva.consultas : 0}
          icon={BuildingOfficeIcon}
          color="primary"
          subtitle={stats.filialMaisAtiva ? 
            `Filial ${stats.filialMaisAtiva.filial}` : 
            'Nenhuma filial'
          }
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consultas por Filial */}
        <ChartContainer title="Distribuição de Consultas por Filial">
          {consultasPorFilial.length > 0 && (
            <PieChart data={consultasPorFilial.slice(0, 10)} />
          )}
        </ChartContainer>

        {/* Produtos por Categoria */}
        <ChartContainer title="Produtos por Categoria">
          {produtosPorCategoria.length > 0 && (
            <PieChart data={produtosPorCategoria} />
          )}
        </ChartContainer>
      </div>

      {/* Timeline */}
      <div className="grid grid-cols-1 gap-6">
        <ChartContainer title="Evolução de Consultas ao Longo do Tempo">
          {timelineData.length > 0 && (
            <LineChart data={timelineData} />
          )}
        </ChartContainer>
      </div>

      {/* Resumos Detalhados */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Filiais */}
        <div className="card-dark p-6">
          <h3 className="text-lg font-semibold text-dark-text mb-4 border-b border-gray-700 pb-2">
            Top 5 Filiais Mais Ativas
          </h3>
          <div className="space-y-3">
            {estatisticasFiliais.slice(0, 5).map((filial, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-primary">
                    #{index + 1}
                  </span>
                  <span className="text-dark-text-secondary">
                    Filial {filial.filial}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-medium text-dark-text">
                    {companyDataService.formatNumber(filial.consultas)} consultas
                  </div>
                  <div className="text-xs text-dark-text-secondary">
                    {filial.produtos} produtos • {filial.dispositivos} dispositivos
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Dispositivos */}
        <div className="card-dark p-6">
          <h3 className="text-lg font-semibold text-dark-text mb-4 border-b border-gray-700 pb-2">
            Top 5 Dispositivos Mais Usados
          </h3>
          <div className="space-y-3">
            {estatisticasDispositivos.slice(0, 5).map((dispositivo, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-primary">
                    #{index + 1}
                  </span>
                  <span className="text-dark-text-secondary text-sm">
                    {dispositivo.dispositivo}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-medium text-dark-text">
                    {companyDataService.formatNumber(dispositivo.consultas)} consultas
                  </div>
                  <div className="text-xs text-dark-text-secondary">
                    {dispositivo.produtos} produtos • {dispositivo.filiais} filiais
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
